import { computed, inject, Injectable } from '@angular/core';
import { KeycloakService } from '@app/security/keycloak/keycloak.service';
import { AuthorizationService } from '@app/security/authorization.service';
import { NavigationService } from '@app/portal/navigation/navigation.service';
import { AppRoute, SphereAppCategory } from '@app/app-routes/__app-route__';
import { LocalStorageService } from '@app/portal/local-storage/local-storage.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { SphereAppCategories } from '@app/app-routes/__app-categories';

export interface ISplitSphereApps {
    all: AppRoute[];
    favorites: AppRoute[];
    apps: AppRoute[];
}

@Injectable({
    providedIn: 'root'
})
export class SphereAppsService {
    public static FavoriteAppsSettingsKey = 'favorite-apps';

    private readonly keycloak = inject(KeycloakService);
    private readonly authorization = inject(AuthorizationService);
    private readonly nav = inject(NavigationService);
    private readonly storage = inject(LocalStorageService);

    public readonly favoriteAppIds = toSignal<string[]>(
        this.storage.watch<string[]>('portal', SphereAppsService.FavoriteAppsSettingsKey, { type: 'array', items: { type: 'string' } }).pipe(
            startWith([]),
            map(ids => {
                if (!ids || !Array.isArray(ids)) {
                    return [];
                }
                return ids;
            })
        )
    );

    public readonly accessibleSphereApps = this.authorization.getAccessibleSphereApps([...this.nav.apps]);
    public readonly favoriteApps = computed(() => {
        return this.favoriteAppIds()
            .map(appName => this.accessibleSphereApps().find(app => app.data.id === appName))
            .filter(app => !!app && !!app.data);
    });
    public readonly appCategories = computed(() =>
        SphereAppCategories.filter(category => {
            try {
                return this.accessibleSphereApps().filter(app => app.data.categories.includes(category.category)).length > 0;
            } catch (e) {
                return false;
            }
        }).sort((prev, next) => {
            return prev > next ? -1 : 1;
        })
    );
    public readonly appsByCategory = computed(() => {
        const categoryMap = new Map<SphereAppCategory, AppRoute[]>();
        for (const app of this.accessibleSphereApps()) {
            for (const category of app.data.categories) {
                if (categoryMap.has(category)) {
                    categoryMap.set(category, [...categoryMap.get(category), app]);
                } else {
                    categoryMap.set(category, [app]);
                }
            }
        }
        return categoryMap;
    });

    public readonly appsForSelectedCategory = (selectedCategory: SphereAppCategory | 'favorites') =>
        computed<ISplitSphereApps>(() => {
            if (!selectedCategory) {
                return this.splitFavoriteApps(this.accessibleSphereApps());
            }
            try {
                if (selectedCategory === 'favorites') {
                    return { favorites: this.favoriteApps(), apps: [], all: this.favoriteApps() };
                } else {
                    return this.splitFavoriteApps(this.appsByCategory().get(selectedCategory as SphereAppCategory) || []);
                }
            } catch (e) {
                console.error(e);
                return { apps: [], favorites: [], all: [] };
            }
        });

    public splitFavoriteApps(_apps: AppRoute[]) {
        const apps = [],
            favorites = [];
        for (const app of _apps) {
            if (this.favoriteApps().findIndex(_app => _app.data.id === app.data.id) >= 0) {
                favorites.push(app);
            } else {
                apps.push(app);
            }
        }
        return { apps, favorites, all: _apps };
    }

    public addFavoriteApp(app: AppRoute) {
        const favoriteApps: string[] = [...this.favoriteAppIds(), app.data.id];
        return this.storage
            .set('portal', SphereAppsService.FavoriteAppsSettingsKey, favoriteApps, { type: 'array', items: { type: 'string' } })
            .subscribe();
    }

    public removeFavoriteApp(app: AppRoute) {
        const favoriteApps: string[] = this.favoriteAppIds().filter(id => id !== app.data.id);
        return this.storage
            .set('portal', SphereAppsService.FavoriteAppsSettingsKey, favoriteApps, { type: 'array', items: { type: 'string' } })
            .subscribe();
    }
}
